// ============================================================
// Auth service — browser side of /api/auth/* (replaces supabase.auth).
//
// Sessions live in httpOnly cookies set by the API server; this module
// only calls the endpoints and broadcasts sign-in / sign-out to other
// tabs (and to `onAuthStateChange` listeners in this tab).
// ============================================================

import { api, apiResult, type ServiceError } from "./http";

/** The user object the API returns (subset of the old Supabase `User`). */
export interface AuthUser {
  id: string;
  email: string;
  user_metadata: Record<string, unknown>;
  app_metadata: Record<string, unknown>;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuthSession {
  user: AuthUser;
}

export type AuthEvent = "SIGNED_IN" | "SIGNED_OUT" | "USER_UPDATED";
type Listener = (event: AuthEvent, session: AuthSession | null) => void;

const listeners = new Set<Listener>();
const CHANNEL = "wacrm-auth";
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = (e: MessageEvent<{ event: AuthEvent; session: AuthSession | null }>) => {
      listeners.forEach((l) => l(e.data.event, e.data.session));
    };
  }
  return channel;
}

function emit(event: AuthEvent, session: AuthSession | null) {
  listeners.forEach((l) => l(event, session));
  getChannel()?.postMessage({ event, session });
}

interface UserResponse {
  user: AuthUser | null;
}

/** Current signed-in user, or null. Never throws. */
export async function getSession(): Promise<{ session: AuthSession | null; error: ServiceError | null }> {
  const { data, error } = await apiResult(api.get<UserResponse>("/auth/session"));
  if (error) return { session: null, error };
  return { session: data.user ? { user: data.user } : null, error: null };
}

export async function getUser(): Promise<{ user: AuthUser | null; error: ServiceError | null }> {
  const { session, error } = await getSession();
  return { user: session?.user ?? null, error };
}

export async function signInWithPassword(credentials: { email: string; password: string }) {
  const { data, error } = await apiResult(api.post<UserResponse>("/auth/login", credentials));
  if (error) return { user: null, error };
  if (data.user) emit("SIGNED_IN", { user: data.user });
  return { user: data.user, error: null };
}

export async function signUp(input: {
  email: string;
  password: string;
  data?: Record<string, unknown>;
}) {
  const { data, error } = await apiResult(api.post<UserResponse>("/auth/signup", input));
  if (error) return { user: null, session: null, error };
  const session = data.user ? { user: data.user } : null;
  if (session) emit("SIGNED_IN", session);
  return { user: data.user, session, error: null };
}

/**
 * Sign out. `scope` mirrors supabase-js:
 *   local  — this browser (default)
 *   global — every device
 *   others — every device except this one
 */
export async function signOut(opts: { scope?: "local" | "global" | "others" } = {}) {
  const { error } = await apiResult(api.post("/auth/logout", { scope: opts.scope ?? "local" }));
  if (!error && opts.scope !== "others") emit("SIGNED_OUT", null);
  return { error };
}

/**
 * Update the signed-in user. Pass `current_password` alongside a new
 * `password` to have the server re-verify the old one first.
 */
export async function updateUser(input: {
  password?: string;
  current_password?: string;
  email?: string;
  data?: Record<string, unknown>;
}) {
  const { data, error } = await apiResult(api.patch<UserResponse>("/auth/user", input));
  if (error) return { user: null, error };
  if (data.user) emit("USER_UPDATED", { user: data.user });
  return { user: data.user, error: null };
}

/** Email a password-reset link (always succeeds for unknown emails). */
export async function resetPasswordForEmail(email: string) {
  const { error } = await apiResult(api.post("/auth/recover", { email }));
  return { error };
}

/** Complete a reset from the emailed link's token. Signs the user in. */
export async function resetPassword(token: string, password: string) {
  const { data, error } = await apiResult(api.post<UserResponse>("/auth/reset-password", { token, password }));
  if (error) return { user: null, error };
  if (data.user) emit("SIGNED_IN", { user: data.user });
  return { user: data.user, error: null };
}

/** Subscribe to sign-in / sign-out in this tab and other tabs. */
export function onAuthStateChange(listener: Listener): { unsubscribe: () => void } {
  getChannel();
  listeners.add(listener);
  return { unsubscribe: () => listeners.delete(listener) };
}
